import React, { useState, useEffect } from 'react';
import { firestore, functions } from '../../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './SuperAdminDashboard.css';

const SuperAdminDashboard = () => {
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);

  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    specialties: '',
    description: '',
    profileImage: '',
    isActive: true,
    createAccount: false,
    password: '',
    sendInviteEmail: true // Nouveau champ pour l'option d'envoi d'email
  });

  // Charger les praticiens
  useEffect(() => {
    const fetchPractitioners = async () => {
      try {
        const snapshot = await firestore
          .collection('practitioners')
          .get();

        const practitionersList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setPractitioners(practitionersList);
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement des praticiens:', error);
        setLoading(false);
      }
    };

    fetchPractitioners();
  }, []);

  // Gérer la sélection d'un praticien pour édition
  const handlePractitionerSelect = (practitioner) => {
    setSelectedPractitioner(practitioner);
    setFormData({
      name: practitioner.name || '',
      title: practitioner.title || '',
      email: practitioner.email || '',
      phone: practitioner.phone || '',
      specialties: practitioner.specialties ? practitioner.specialties.join(', ') : '',
      description: practitioner.description || '',
      profileImage: practitioner.profileImage || '',
      isActive: practitioner.isActive !== false,
      createAccount: false,
      password: '',
      sendInviteEmail: false // Pas besoin d'envoyer un email pour une édition
    });
    setIsAddingNew(false);
    setStatusMessage(null);
  };

  // Gérer l'ajout d'un nouveau praticien
  const handleAddNew = () => {
    setSelectedPractitioner(null);
    setFormData({
      name: '',
      title: '',
      email: '',
      phone: '',
      specialties: '',
      description: '',
      profileImage: '',
      isActive: true,
      createAccount: true,
      password: '',
      sendInviteEmail: true // Par défaut, on envoie un email d'invitation
    });
    setIsAddingNew(true);
    setStatusMessage(null);
  };

  // Mettre à jour les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

    // Créer un compte pour le praticien en utilisant Firebase Admin SDK via Cloud Functions
    const createPractitionerAccount = async (email, password, name) => {
      try {
        // Appel à une Cloud Function pour créer l'utilisateur sans déconnecter l'admin actuel
        const createUserFunction = httpsCallable(functions, 'createUserAccount');
        const result = await createUserFunction({
          email: email,
          password: password,
          name: name,
          role: 'practitioner'
        });
        
        return result.data.uid; // Retourne l'ID de l'utilisateur créé
      } catch (error) {
        console.error('Erreur lors de la création du compte:', error);
        throw error;
      }
    };
    
  // Sauvegarder le praticien
  const handleSavePractitioner = async (e) => {
    e.preventDefault();
    setStatusMessage(null);

    try {
      // Préparer les données à sauvegarder
      const practitionerData = {
        name: formData.name,
        title: formData.title,
        email: formData.email,
        phone: formData.phone,
        specialties: formData.specialties.split(',').map(s => s.trim()).filter(s => s),
        description: formData.description,
        profileImage: formData.profileImage,
        isActive: formData.isActive,
        updatedAt: new Date()
      };

      if (isAddingNew) {
        // Créer un compte utilisateur si demandé
        let userId = null;

        if (formData.createAccount && formData.email && formData.password) {
          try {
            // Utiliser la fonction Cloud pour créer l'utilisateur
            userId = await createPractitionerAccount(
              formData.email,
              formData.password,
              formData.name
            );
            
            // Créer le document utilisateur dans Firestore
            await firestore.collection('users').doc(userId).set({
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              role: 'practitioner',
              createdAt: new Date()
            });
          } catch (authError) {
            console.error('Erreur lors de la création du compte:', authError);
            alert(`Erreur lors de la création du compte: ${authError.message}`);
            return;
          }
        }

        // Ajouter le praticien à Firestore
        const practitionerRef = await firestore
          .collection('practitioners')
          .add({
            ...practitionerData,
            userId,
            createdAt: new Date(),
            invitationSent: false // Par défaut, aucune invitation n'a été envoyée
          });

        if (userId) {
          // Mettre à jour l'utilisateur avec l'ID du praticien
          await firestore
            .collection('users')
            .doc(userId)
            .update({
              practitionerId: practitionerRef.id
            });
        }

        // Mettre à jour l'état local
        const newPractitioner = {
          id: practitionerRef.id,
          ...practitionerData,
          userId,
          createdAt: new Date()
        };

        setPractitioners([
          ...practitioners,
          newPractitioner
        ]);

        // Si l'option d'envoi d'email est activée et un compte utilisateur a été créé
        if (formData.sendInviteEmail && userId) {
          try {
            // Appeler la fonction Cloud pour envoyer l'email d'invitation
            const functions = getFunctions();
            const sendInvite = httpsCallable(functions, 'sendPractitionerInvite');

            await sendInvite({
              practitioner: {
                name: formData.name,
                email: formData.email
              },
              practitionerId: practitionerRef.id,
              email: formData.email
            });

            // Mettre à jour l'état local pour indiquer que l'invitation a été envoyée
            newPractitioner.invitationSent = true;
            newPractitioner.invitationSentAt = new Date();

            setStatusMessage({
              type: 'success',
              text: 'Praticien ajouté avec succès et email d\'invitation envoyé.'
            });
          } catch (emailError) {
            console.error('Erreur lors de l\'envoi de l\'email d\'invitation:', emailError);
            setStatusMessage({
              type: 'warning',
              text: 'Praticien ajouté, mais erreur lors de l\'envoi de l\'email d\'invitation.'
            });
          }
        } else {
          setStatusMessage({
            type: 'success',
            text: 'Praticien ajouté avec succès.'
          });
        }
      } else if (selectedPractitioner) {
        // Mettre à jour un praticien existant
        await firestore
          .collection('practitioners')
          .doc(selectedPractitioner.id)
          .update(practitionerData);

        // Mettre à jour l'état local
        setPractitioners(practitioners.map(p => {
          if (p.id === selectedPractitioner.id) {
            return {
              ...p,
              ...practitionerData
            };
          }
          return p;
        }));

        setStatusMessage({
          type: 'success',
          text: 'Informations du praticien mises à jour avec succès.'
        });
      }

      // Réinitialiser le formulaire
      setIsAddingNew(false);
      setSelectedPractitioner(null);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du praticien:', error);
      setStatusMessage({
        type: 'error',
        text: `Erreur: ${error.message}`
      });
    }
  };

  // Envoyer manuellement une invitation à un praticien existant
  const handleSendInvite = async (practitioner) => {
    try {
      // Appeler la fonction Cloud pour envoyer l'email d'invitation
      const functions = getFunctions();
      const sendInvite = httpsCallable(functions, 'sendPractitionerInvite');

      await sendInvite({
        practitioner: {
          name: practitioner.name,
          email: practitioner.email
        },
        practitionerId: practitioner.id,
        email: practitioner.email
      });

      // Mettre à jour l'état local
      setPractitioners(practitioners.map(p => {
        if (p.id === practitioner.id) {
          return {
            ...p,
            invitationSent: true,
            invitationSentAt: new Date()
          };
        }
        return p;
      }));

      setStatusMessage({
        type: 'success',
        text: `Email d'invitation envoyé à ${practitioner.email}`
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'email d\'invitation:', error);
      setStatusMessage({
        type: 'error',
        text: `Erreur lors de l'envoi de l'email: ${error.message}`
      });
    }
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setIsAddingNew(false);
    setSelectedPractitioner(null);
  };

  if (loading) {
    return <div className="loading">Chargement des praticiens...</div>;
  }

  return (
    <div className="super-admin-dashboard">
      <h1>Administration</h1>

      {/* Affichage des messages de statut */}
      {statusMessage && (
        <div className={`status-message ${statusMessage.type}`}>
          {statusMessage.text}
        </div>
      )}

      <div className="admin-content">
        <div className="practitioners-management">
          <div className="practitioners-header">
            <h2>Gestion des praticiens</h2>
            <button className="btn btn-primary" onClick={handleAddNew}>
              Ajouter un praticien
            </button>
          </div>

          {practitioners.length === 0 ? (
            <div className="no-practitioners">
              <p>Aucun praticien enregistré.</p>
              <button className="btn btn-primary" onClick={handleAddNew}>
                Ajouter votre premier praticien
              </button>
            </div>
          ) : (
            <div className="practitioners-table-container">
              <table className="practitioners-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Titre</th>
                    <th>Contact</th>
                    <th>Spécialités</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {practitioners.map(practitioner => (
                    <tr key={practitioner.id}>
                      <td>{practitioner.name}</td>
                      <td>{practitioner.title}</td>
                      <td>
                        <div>{practitioner.email}</div>
                        <div>{practitioner.phone}</div>
                      </td>
                      <td>
                        {practitioner.specialties && practitioner.specialties.map(specialty => (
                          <span key={specialty} className="specialty-tag">{specialty}</span>
                        ))}
                      </td>
                      <td>
                        <span className={`status ${practitioner.isActive ? 'active' : 'inactive'}`}>
                          {practitioner.isActive ? 'Actif' : 'Inactif'}
                        </span>
                        {practitioner.invitationSent && (
                          <span className="invitation-sent" title="Invitation envoyée"> ✉️</span>
                        )}
                      </td>
                      <td>
                        <div className="practitioner-actions">
                          <button
                            className="btn-icon"
                            onClick={() => handlePractitionerSelect(practitioner)}
                          >
                            ✏️
                          </button>

                          {/* Bouton pour envoyer une invitation si le praticien a un userId mais n'a pas reçu d'invitation */}
                          {practitioner.userId && !practitioner.invitationSent && (
                            <button
                              className="btn-icon"
                              onClick={() => handleSendInvite(practitioner)}
                              title="Envoyer une invitation par email"
                            >
                              ✉️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {(isAddingNew || selectedPractitioner) && (
          <div className="practitioner-form-container">
            <h3>{isAddingNew ? 'Ajouter un praticien' : 'Modifier le praticien'}</h3>

            <form onSubmit={handleSavePractitioner} className="practitioner-form">
              <div className="form-group">
                <label htmlFor="name">Nom complet</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="title">Titre professionnel</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-control"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Téléphone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-control"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="specialties">Spécialités (séparées par des virgules)</label>
                <input
                  type="text"
                  id="specialties"
                  name="specialties"
                  className="form-control"
                  value={formData.specialties}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  className="form-control"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="4"
                ></textarea>
              </div>

              <div className="form-group">
                <label htmlFor="profileImage">URL de l'image de profil</label>
                <input
                  type="url"
                  id="profileImage"
                  name="profileImage"
                  className="form-control"
                  value={formData.profileImage}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  Praticien actif
                </label>
              </div>

              {isAddingNew && (
                <div className="account-section">
                  <div className="section-header">
                    <h4>Compte utilisateur</h4>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="createAccount"
                        checked={formData.createAccount}
                        onChange={handleInputChange}
                      />
                      Créer un compte d'accès
                    </label>
                  </div>

                  {formData.createAccount && (
                    <>
                      <div className="form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                          type="password"
                          id="password"
                          name="password"
                          className="form-control"
                          value={formData.password}
                          onChange={handleInputChange}
                          required={formData.createAccount}
                          minLength="8"
                        />
                        <div className="text-muted">
                          Au moins 8 caractères
                        </div>
                      </div>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="sendInviteEmail"
                            checked={formData.sendInviteEmail}
                            onChange={handleInputChange}
                          />
                          Envoyer un email d'invitation
                        </label>
                        <div className="text-muted">
                          Le praticien recevra un email avec un lien pour définir son mot de passe.
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-cancel" onClick={handleCancelEdit}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {isAddingNew ? 'Ajouter' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminDashboard;